import React, { useState, useEffect } from 'react';
import { Download, AlertCircle } from 'lucide-react';
import { paymentHistoryService } from '../../services/paymentHistoryService';
import PaymentTimeline from './components/PaymentTimeline';
import PaymentSummary from './components/PaymentSummary';
import NegotiationSelector from './components/NegotiationSelector';
import FilterToolbar from './components/FilterToolbar';

export default function PaymentHistoryAndTimelineTracking() {
  const [negotiations, setNegotiations] = useState([]);
  const [selectedNegotiation, setSelectedNegotiation] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    dateRange: 'all',
    operator: 'all',
    amountRange: { min: 0, max: 999999 }
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [negotiationsData, statsData] = await Promise.all([
        paymentHistoryService?.getAllNegotiationsWithPayments(),
        paymentHistoryService?.getPaymentStatistics()
      ]);

      setNegotiations(negotiationsData);
      setStatistics(statsData);
      
      if (negotiationsData?.length > 0) {
        setSelectedNegotiation(negotiationsData?.[0]);
      }
    } catch (err) {
      setError(err?.message);
      console.error('Error loading payment history:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleNegotiationSelect = (negotiation) => {
    setSelectedNegotiation(negotiation);
  };

  const handlePaymentStatusUpdate = async (receivableId, newStatus) => {
    try {
      await paymentHistoryService?.updatePaymentStatus(
        receivableId, 
        newStatus,
        newStatus === 'paid' ? new Date()?.toISOString() : null
      );
      
      await loadData();
    } catch (err) {
      setError(err?.message);
    }
  };

  const filteredNegotiations = negotiations?.filter(neg => {
    if (filters?.status !== 'all' && neg?.paymentStatus !== filters?.status) {
      return false;
    }
    
    if (filters?.operator !== 'all') {
      const hasOperator = neg?.receivables?.some(r => r?.operator === filters?.operator);
      if (!hasOperator) return false;
    }
    
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando histórico de pagamentos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <div className="flex items-center gap-3 mb-3">
            <AlertCircle className="text-red-600" size={24} />
            <h3 className="text-red-800 font-semibold">Erro ao carregar dados</h3>
          </div>
          <p className="text-red-700 mb-4">{error}</p>
          <button 
            onClick={loadData}
            className="w-full bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Histórico de Pagamentos</h1>
            <p className="text-sm text-gray-500 mt-1">
              Acompanhamento detalhado de todos os pagamentos por negociação
            </p>
          </div>
          <button className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
            <Download size={18} />
            Exportar Relatório
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex h-[calc(100vh-120px)]">
        {/* Left Sidebar - Negotiation Selector (20%) */}
        <div className="w-1/5 bg-white border-r border-gray-200 overflow-y-auto">
          <NegotiationSelector
            negotiations={filteredNegotiations}
            selectedNegotiation={selectedNegotiation}
            onSelect={handleNegotiationSelect}
          />
        </div>

        {/* Center - Payment Timeline (60%) */}
        <div className="w-3/5 bg-gray-50 overflow-y-auto">
          <div className="p-6">
            <FilterToolbar 
              filters={filters} 
              onFiltersChange={setFilters}
              onFilterChange={setFilters}
              onSaveView={() => {}}
            />
            
            {selectedNegotiation ? (
              <PaymentTimeline
                negotiation={selectedNegotiation}
                onStatusUpdate={handlePaymentStatusUpdate}
              />
            ) : (
              <div className="flex items-center justify-center h-96">
                <p className="text-gray-500">Selecione uma negociação para ver os pagamentos</p>
              </div>
            )}
          </div>
        </div>

        {/* Right Sidebar - Payment Summary (20%) */}
        <div className="w-1/5 bg-white border-l border-gray-200 overflow-y-auto">
          <PaymentSummary statistics={statistics} />
        </div>
      </div>
    </div>
  );
}